import { centsToLocalizedString } from "../../../../../lib/Money.ts";
import type { SalesInvoiceResponse } from "../CreateInvoicePage/salesInvoiceApi.ts";

export const NOTHING_LEFT_TO_CREDIT = 0;

export type CreditableLine = {
    id: string;
    description: string;
    originalQuantity: number;
    remainingQuantity: number;
    quantity: string;
    unitPriceCents: number;
    removed: boolean;
};

export function toCreditableLines(invoice: SalesInvoiceResponse): CreditableLine[] {
    return invoice.lines.map((line) => {
        const remainingQuantity = Number(line.remaining_creditable_quantity);
        return {
            id: String(line.id),
            description: line.description,
            originalQuantity: Number(line.quantity),
            remainingQuantity,
            quantity: String(remainingQuantity),
            unitPriceCents: Number(line.unit_price_net_cents),
            removed: remainingQuantity <= NOTHING_LEFT_TO_CREDIT,
        };
    });
}

export function isCreditable(line: CreditableLine): boolean {
    return line.remainingQuantity > NOTHING_LEFT_TO_CREDIT;
}

export function describeRemainder(line: CreditableLine): string {
    if (!isCreditable(line)) {
        return "Volledig gecrediteerd";
    }
    if (line.remainingQuantity === line.originalQuantity) {
        return `${line.originalQuantity} × € ${centsToLocalizedString(line.unitPriceCents)}`;
    }
    return (
        `Nog ${line.remainingQuantity} van ${line.originalQuantity} ` +
        `× € ${centsToLocalizedString(line.unitPriceCents)}`
    );
}

export function findUncreditableLine(lines: CreditableLine[]): CreditableLine | undefined {
    return lines.find((line) => {
        const quantity = Number(line.quantity);
        return (
            !Number.isFinite(quantity)
            || quantity <= NOTHING_LEFT_TO_CREDIT
            || quantity > line.remainingQuantity
        );
    });
}

export function totalToCreditInCents(lines: CreditableLine[]): number {
    return Math.round(
        lines.reduce(
            (sum, line) => sum + line.unitPriceCents * (Number(line.quantity) || 0),
            0,
        ),
    );
}

export function creditsEverythingThatIsLeft(
    keptLines: CreditableLine[],
    creditableLines: CreditableLine[],
): boolean {
    return (
        keptLines.length === creditableLines.length
        && keptLines.every((line) => Number(line.quantity) === line.remainingQuantity)
    );
}
